import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import api from "../api/axios";
import { useSocket } from "../socket/SocketProvider";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext"; // 👈 IMPORTANT
import {
  nextActiveChatsAfterIncoming,
  ChatListItem,
} from "../chat/chatActiveChats";
import { normalizeMessage } from "../chat/chatMessageUtils";
import { idOf } from "../chat/idOf";
import { decryptMessage } from "../crypto/simpleE2EE";
import { requireMySecret } from "../chat/chatAsync";

/* ---------------- context types ---------------- */

type ActiveChatsContextValue = {
  activeChats: ChatListItem[];
  setActiveChats: React.Dispatch<React.SetStateAction<ChatListItem[]>>;
  refreshActiveChats: () => Promise<void>;
  markChatAsRead: (chatUserId: string) => void;
};

const ActiveChatsContext = createContext<ActiveChatsContextValue | null>(null);

/* ---------------- provider ---------------- */

export const ActiveChatsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { socket, connected } = useSocket();
  const { user, e2eeReady } = useAuth();
  const { activeChatUserId } = useChat(); // 👈 used to detect open thread

  const [activeChats, setActiveChats] = useState<ChatListItem[]>([]);
  const myId = idOf(user);

  /* -------- fetch active chats (initial list) -------- */

  const refreshActiveChats = useCallback(async () => {
    if (!user?._id) return;

    try {
      const res = await api.get("/message/active-chats");

      const normalized: ChatListItem[] = (Array.isArray(res.data)
        ? res.data
        : []
      )
        .map((x: any) => ({
          _id: String(
            x._id ||
              x.userId ||
              x.otherUserId ||
              x.other?._id ||
              x.partnerId ||
              ""
          ),
          profilename:
            x.profilename ||
            x.name ||
            x.other?.profilename ||
            x.other?.name ||
            "User",
          picture: x.picture || x.other?.picture || "",
          lastMessage: x.lastMessage || x.preview || "",
          lastMessageType: x.lastMessageType || "text",
          unread:
            typeof x.unreadCount === "number"
              ? x.unreadCount
              : typeof x.unread === "number"
              ? x.unread
              : 0,
          lastMessageAt:
            x.lastMessageAt ??
            x.lastMessageAtMs ??
            x.createdAt ??
            x.last?.createdAt ??
            x.last?.timestamp ??
            undefined,
        }))
        .filter((c) => c._id && c._id !== myId);

      setActiveChats(normalized);
    } catch (err) {
      console.error("Failed to fetch active chats:", err);
    }
  }, [user?._id, myId]);

  /* -------- mark read locally -------- */

  const markChatAsRead = useCallback((chatUserId: string) => {
    setActiveChats((prev) =>
      prev.map((c) =>
        c._id === chatUserId ? { ...c, unread: 0 } : c
      )
    );
  }, []);

  /* -------- initial load -------- */

  useEffect(() => {
    refreshActiveChats();
  }, [refreshActiveChats]);

  /* -------- refresh on reconnect (very important) -------- */

  useEffect(() => {
  if (!socket) return;

  const onConnect = () => {
    refreshActiveChats();
  };

  socket.on("connect", onConnect);

  return () => {
    socket.off("connect", onConnect);
  };
}, [socket, refreshActiveChats]);


  /* -------- socket → update last message preview -------- */

  useEffect(() => {
    if (!socket || !connected || !user?._id || !e2eeReady) return;

    let cancelled = false;

    const onIncomingMessage = async (raw: any) => {
      const msg = normalizeMessage(raw);

      const iAmSender = idOf(msg.sender) === myId;
      const partnerId = iAmSender
        ? idOf(msg.recipient)
        : idOf(msg.sender);

      const isOpenThread =
        !!activeChatUserId &&
        String(activeChatUserId) === String(partnerId);

      let previewText = "";

      try {
        if (msg.isEncrypted) {
          const env = iAmSender
            ? (msg as any).selfEnvelope
            : (msg as any).encrypted;

          if (env) {
            const secret = await requireMySecret();
            previewText = await decryptMessage(env, secret);
          }
        } else {
          previewText =
            (msg as any).decryptedText ||
            msg.text ||
            (msg as any).message ||
            "";
        }
      } catch (e) {
        console.warn("Preview decrypt failed", e);
      }

      if (cancelled) return;

      setActiveChats((prev) =>
        nextActiveChatsAfterIncoming(
          prev,
          {
            ...msg,
            decryptedText: previewText,
            text: previewText,
          } as any,
          myId,
          isOpenThread, // 👈 FIXED
          {
            message: "Message",
            photo: "Photo",
            video: "Video",
            audio: "Audio",
            media: "Media",
            fallbackUser: "User",
          }
        )
      );

      // clear unread instantly if chat is open
      if (isOpenThread) {
        markChatAsRead(partnerId);
      }
    };

    socket.on("message", onIncomingMessage);

    return () => {
      cancelled = true;
      socket.off("message", onIncomingMessage);
    };
  }, [
    socket,
    connected,
    user?._id,
    myId,
    e2eeReady,
    activeChatUserId,
    markChatAsRead,
  ]);

  /* -------- provider -------- */

  return (
    <ActiveChatsContext.Provider
      value={{
        activeChats,
        setActiveChats,
        refreshActiveChats,
        markChatAsRead,
      }}
    >
      {children}
    </ActiveChatsContext.Provider>
  );
};

/* ---------------- hook ---------------- */

export const useActiveChats = () => {
  const ctx = useContext(ActiveChatsContext);
  if (!ctx) {
    throw new Error("useActiveChats must be used inside ActiveChatsProvider");
  }
  return ctx;
};
